// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function addCopyDisclaimers(text, before = '', after = '') {
  return [before, text, after].filter(hasText).join('\n\n');
}

function selectionIntersectsNode(selection, node) {
  if (!selection || selection.rangeCount === 0) return false;

  for (let i = 0; i < selection.rangeCount; i += 1) {
    const range = selection.getRangeAt(i);
    try {
      if (range.intersectsNode(node)) return true;
    } catch {
      if (node.contains(range.commonAncestorContainer)) return true;
    }
  }

  return false;
}

function selectionIntersectsAnyNode(selection, nodes) {
  return nodes.some((node) => selectionIntersectsNode(selection, node));
}

function getSelectionContainer(selectionNode) {
  if (!selectionNode) return null;
  return selectionNode.nodeType === Node.ELEMENT_NODE ? selectionNode : selectionNode.parentElement;
}

function getDisclaimerOptionsForSelection(selection, currentOptions) {
  const disclaimersBySelector = currentOptions.disclaimersBySelector ?? [];
  const selectionContainers = [
    getSelectionContainer(selection.anchorNode),
    getSelectionContainer(selection.focusNode),
  ].filter(Boolean);

  const matchingDisclaimerOptions = disclaimersBySelector.filter(({ selector }) =>
    selectionContainers.every((container) => container.closest(selector)),
  );

  if (matchingDisclaimerOptions.length === 1) {
    return matchingDisclaimerOptions[0];
  }

  return currentOptions;
}

function removeExcludedNodes(fragment, excludeSelectors) {
  if (!excludeSelectors || excludeSelectors.length === 0) return;

  fragment.querySelectorAll(excludeSelectors.join(',')).forEach((excludedNode) => {
    excludedNode.remove();
  });
}

function getSelectionText(selection, excludeSelectors = []) {
  if (!selection || selection.rangeCount === 0) return '';

  const fragments = [];
  for (let i = 0; i < selection.rangeCount; i += 1) {
    const container = document.createElement('div');
    container.append(selection.getRangeAt(i).cloneContents());
    removeExcludedNodes(container, excludeSelectors);
    fragments.push(container.innerText ?? container.textContent ?? '');
  }

  return fragments.join('\n').trim();
}

function writeClipboardText(event, text) {
  if (event.clipboardData) {
    event.clipboardData.setData('text/plain', text);
    event.preventDefault();
    return;
  }

  if (navigator.clipboard) {
    event.preventDefault();
    navigator.clipboard.writeText(text);
  }
}

export function copyWithDisclaimers(node, options = {}) {
  let currentOptions = options;

  function handleCopy(event) {
    const selection = window.getSelection();
    const selectors = currentOptions.selectors ?? [];
    const copyNodes = selectors.length > 0 ? Array.from(node.querySelectorAll(selectors.join(','))) : [node];

    if (!selectionIntersectsAnyNode(selection, copyNodes)) return;

    const copiedText = getSelectionText(selection, currentOptions.excludeSelectors);
    if (!hasText(copiedText)) return;

    const disclaimerOptions = getDisclaimerOptionsForSelection(selection, currentOptions);
    const clipboardText = addCopyDisclaimers(copiedText, disclaimerOptions.before ?? '', disclaimerOptions.after ?? '');
    writeClipboardText(event, clipboardText);
  }

  document.addEventListener('copy', handleCopy);

  return {
    update(nextOptions = {}) {
      currentOptions = nextOptions;
    },
    destroy() {
      document.removeEventListener('copy', handleCopy);
    },
  };
}
